// SubagentsListPanel — Phase 12.3 panel inside the People hub.
// Was AdminMyAgents.tsx (agent's view of own direct sub-agents).
//
// Lists DIRECT child agents only (indirect ones are visible via the
// chain panel on the My Pool dashboard, but cannot be edited here).
// "+ New sub-agent" creates one under the calling agent.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminAvatar from '@baaligames/shared-ui/admin/components/AdminAvatar';
import Icon from '@baaligames/shared-ui/components/Icon';
import PhoneContact from '@baaligames/shared-ui/components/PhoneContact';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { useT } from '@baaligames/shared-ui/lib/i18n';
import AcceptingAgentsToggle from './AcceptingAgentsToggle';
import { INPUT_CLS, INPUT_DATE_CLS } from '../../components/fieldStyles';
import CommissionRatesFields, { type CommissionRates, ratesPayload } from '@baaligames/shared-ui/admin/components/CommissionRatesFields';
import LocationCapture, {
  type GeoBag,
  EMPTY_GEO_BAG,
  isGeoBagComplete,
} from '@baaligames/shared-ui/components/LocationCapture';

// 2026-05-18 — Backend now strips username + email + first/last from
// the peer-facing /admin/me-agent/sub-agents response. Each row
// instead surfaces a pre-composed `display_name`. We keep the
// optional `username` / `email` field types here for resilience —
// any legacy backend that still includes them won't break the panel
// — but the rendered card uses `display_name` exclusively.
type SubAgent = {
  id: number;
  display_name: string | null;
  // Phase C4 — Direct downline → Identity Stack visible. Backend now
  // returns username + phone for parent-of-direct-sub-agent views.
  // (For indirect downline, the masked endpoint keeps these null.)
  username?: string | null;
  phone?: string | null;
  email?: string | null;
  avatar_url: string | null;
  level: number;
  status: 'active' | 'suspended';
  suspended_at: string | null;
  lock_reason: string | null;
  created_at: string;
  wallet_balance: number;
  direct_players: number;
  direct_agents: number;
  indirect_players: number;
  indirect_agents: number;
  // Phase R5 — sub-agent's Unique Agent ID. Parent agents see this
  // (read-only) so they can verify a downline target before kicking
  // off a transfer to/from one of their child agents.
  uaid: string | null;
};

const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN')}`;

const LEVEL_NAMES: Record<number, string> = { 1: 'Super Master', 2: 'Master', 3: 'Agent' };

export default function SubagentsListPanel() {
  const t = useT();
  const { canDo, admin } = useAdminAuth();
  const myLevel = Number(admin?.level ?? 1);
  // The level of the downline THIS agent creates: Super Master → Master, Master →
  // Agent. An Agent (L3) is a leaf and creates NO sub-agents (guarded below + route
  // + nav + server). childLevelName drives the button / title so the operator knows.
  const childLevelName = LEVEL_NAMES[myLevel + 1] || 'sub-agent';
  const canCreate = canDo('create_subagent', 'edit') && myLevel < 3;

  const [rows, setRows] = useState<SubAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');

  // Client-side filter — the sub-agents endpoint returns the full
  // (small) direct-downline list in one shot, so we filter in-memory
  // over name / @username / phone / UAID / #id. Only surfaced once the
  // roster is big enough to be worth searching (keeps a 1–2 item list
  // uncluttered). Effortless-UX rule: the fewest controls that still work.
  const SEARCH_THRESHOLD = 5;
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((a) =>
      (a.display_name || '').toLowerCase().includes(q) ||
      (a.username || '').toLowerCase().includes(q) ||
      (a.phone || '').toLowerCase().includes(q) ||
      (a.uaid || '').toLowerCase().includes(q) ||
      String(a.id).includes(q)
    );
  }, [rows, search]);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await adminApi.get<{ rows: SubAgent[]; total: number }>(
        '/admin/me-agent/sub-agents'
      );
      setRows(r.rows);
    } catch (e) { setErr((e as ApiError).message || t('agent.activity.error.loadFailed')); }
    finally { setLoading(false); }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      {/* Phase R7 (2026-05-13): "Accepting sub-agents" descriptive
          card — full body copy mirrors the players-side
          AcceptingPlayersToggleCard. Sits at the top of the panel
          so a parent agent reading down the page understands the
          toggle's effect before they look at their sub-agent list. */}
      <AcceptingAgentsToggle />

      {/* Header row with + New sub-agent button (was the AdminLayout `right` prop) */}
      <div className="flex items-center justify-between gap-3">
        <p className="text-[12.5px] text-arena-600 dark:text-neon-200/80">
          The <b className="text-arena-800 dark:text-neon-200">{childLevelName}s</b> you add work under you — set their partnership (P&amp;L) % right here when you create them (capped to your own).
        </p>
        {canCreate && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="shrink-0 inline-flex items-center gap-1 rounded-full
                       bg-emerald-600 hover:bg-emerald-500
                       text-white text-[12px] font-bold px-3 py-1.5
                       shadow-[0_2px_10px_rgba(16,185,129,0.35)] transition"
          >
            <Icon name="add" size={14} /> New {childLevelName}
          </button>
        )}
      </div>

      {/* Search — only once the roster is big enough to warrant it. */}
      {rows.length >= SEARCH_THRESHOLD && (
        <div className="relative">
          <Icon name="search" size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-arena-600 dark:text-neon-200/80 pointer-events-none" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('agent.subAgentsList.searchPlaceholder')}
            className="w-full rounded-full pl-9 pr-3 py-2 text-[13px]
                       bg-white dark:bg-arena-700/40
                       border border-arena-200/60 dark:border-arena-600/50
                       text-arena-900 dark:text-white
                       placeholder-neutral-400 dark:placeholder-white/40
                       focus:outline-none focus:ring-2 focus:ring-violet-400/40 focus:border-violet-400/40"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2
                                                              p-1 rounded-full hover:bg-arena-100/60 dark:hover:bg-arena-600/55">
              <Icon name="close" size={14} className="text-arena-600 dark:text-neon-200/80" />
            </button>
          )}
        </div>
      )}

      {err && (
        <div className="rounded-2xl bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-400/30 text-rose-800 dark:text-rose-200 px-4 py-3 text-sm">
          {err}
        </div>
      )}

      {loading && rows.length === 0 && (
        <div className="text-center py-8 text-arena-600 dark:text-neon-200/70 text-sm">{t('agent.subAgentsList.loading')}</div>
      )}

      {!loading && rows.length === 0 && (
        <div className="rounded-2xl bg-white dark:bg-arena-700/40
                        border border-arena-200/60 dark:border-arena-600/50 p-8 text-center">
          <div className="mx-auto w-14 h-14 rounded-2xl grid place-items-center
                          bg-emerald-50 text-emerald-600
                          dark:bg-emerald-400/15 dark:text-emerald-300 mb-3">
            <Icon name="support_agent" size={26} filled />
          </div>
          <h3 className="text-sm font-bold text-arena-900 dark:text-white">No {childLevelName.toLowerCase()}s yet</h3>
          <p className="text-xs text-arena-600 dark:text-neon-200/80 max-w-md mx-auto mt-1">
            {canCreate
              ? `Tap "+ New ${childLevelName}" to create one. They'll show up here with their own pool, players, and earnings — managed by you.`
              : t('agent.subAgentsList.empty.body.cantCreate')}
          </p>
        </div>
      )}

      {/* Phase 16 (2026-05-12): single-column stack on every breakpoint —
          on desktop the cards stretched to ~1/3 width and the metric
          rows wrapped/clipped, on mobile the layout already worked.
          Keeping it consistently full-width (capped by an outer
          container) preserves the wide horizontal-rectangle feel
          across sizes. Cards stay scannable; long usernames + UAID
          + metric rows have room to breathe. */}
      {rows.length > 0 && filtered.length === 0 && (
        <div className="rounded-2xl bg-white dark:bg-arena-700/40 border border-arena-200/60 dark:border-arena-600/50 p-6 text-center">
          <Icon name="search_off" size={26} className="text-arena-500 dark:text-neon-200/60 mx-auto mb-1.5" />
          <p className="text-[12.5px] text-arena-700 dark:text-neon-200/80">{t('agent.subAgentsList.noMatch')}</p>
        </div>
      )}

      {filtered.length > 0 && (
        <div className="space-y-3">
          {filtered.map((a) => (
            <SubAgentCard key={a.id} a={a} />
          ))}
        </div>
      )}

      {creating && (
        <CreateSubAgentModal childLevelName={childLevelName} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />
      )}
    </div>
  );
}

function SubAgentCard({ a }: { a: SubAgent }) {
  const t = useT();
  return (
    <Link to={`/people/agents/${a.id}`}
          className="block rounded-2xl p-4
                     bg-white dark:bg-arena-700/40
                     border border-arena-200/60 dark:border-arena-600/50
                     hover:bg-arena-50/40 dark:hover:bg-arena-600/40 transition">
      {/* Phase 16 (2026-05-12): on md+ flow identity + metrics side-by-side
          so the wide card uses its horizontal space. On mobile the
          metrics drop below identity (original stacked layout). All
          muted text now carries a dark: override so dark mode reads
          legibly — the previous bare `text-arena-{500,600}` classes
          rendered as near-black on the arena-700/40 card surface. */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-5">
        {/* Identity block — avatar + display_name + level + badges + UAID.
            2026-05-18 — Identity masking. Username and email are NOT
            rendered here; peers see only the structured display name
            (or '—' placeholder until the sub-agent completes their
            profile). The avatar still passes a string to AdminAvatar
            (used as seed for the colored fallback initials), so we
            pass the display name through. */}
        <div className="flex items-start gap-3 min-w-0 md:flex-[2]">
          <AdminAvatar url={a.avatar_url} username={(a.display_name || '').trim() || a.username || '—'} size="md" />
          <div className="min-w-0 flex-1">
            <div className="text-[14px] font-semibold text-arena-900 dark:text-white truncate flex items-center gap-1.5 flex-wrap">
              {/* 2026-05-25 — fall back to username so an incomplete-profile
                  sub-agent shows an identity instead of a bare "—". */}
              <span className={(a.display_name || '').trim() ? '' : 'opacity-60'}>
                {(a.display_name || '').trim() || a.username || '—'}
              </span>
              {/* Level chip removed — agents must not see sub-agent levels
                  (only admin can; policy 2026-05-25). */}
              {a.suspended_at && (
                <span className="text-[9.5px] font-bold uppercase tracking-wider text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-400/15 px-1.5 py-0.5 rounded">
                  {t('agent.subAgentsList.card.suspendedBadge')}
                </span>
              )}
            </div>
            {/* Phase C4 — Identity Stack for DIRECT downline. Username
                + phone are returned by the backend for the parent-of-
                direct-sub-agent endpoint. Same 3-line format the queues
                use. The UAID row stays beneath as a separate identifier. */}
            {a.username ? (
              <div className="text-[11px] text-arena-600 dark:text-neon-200/70 truncate font-mono">
                @{a.username} <span className="text-arena-400 dark:text-neon-200/40">·</span> #{a.id}
              </div>
            ) : (
              <div className="text-[11px] text-arena-600 dark:text-neon-200/70 truncate">#{a.id}</div>
            )}
            {a.phone && (
              <PhoneContact phone={a.phone} name={a.username ?? a.display_name} iconSize={10}
                className="text-[11px] text-arena-700 dark:text-neon-200/80 truncate tabular-nums inline-flex items-center gap-1 hover:text-arena-900 dark:hover:text-neon-200" />
            )}
            {/* Phase R5 — read-only UAID strip. Parents can see their
                child agents' UAIDs (per the locked R5 spec) without
                the option to share them out — that's the child's call. */}
            {a.uaid && (
              <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-mono tracking-[.16em]
                              text-cyan-700 dark:text-cyan-300/90">
                <Icon name="badge" size={10} className="opacity-70" />
                {a.uaid}
              </div>
            )}
          </div>
        </div>

        {/* Metrics block — stacked rows on mobile (full-width below identity),
            inline 3-column row on md+ (right side of the card). Border-top
            visible only on mobile where metrics are stacked below identity. */}
        <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-3 md:flex-[3]
                        gap-x-3 gap-y-1.5
                        border-t md:border-t-0 md:border-l border-arena-200/40 dark:border-arena-600/40
                        pt-3 md:pt-0 md:pl-5 text-[11px]">
          <div className="flex items-center gap-1.5">
            <Icon name="support_agent" size={12} className="text-emerald-600 dark:text-emerald-400" />
            <span className="text-arena-600 dark:text-neon-200/70">{t('agent.subAgentsList.column.agents')}</span>
            <span className="font-bold tabular-nums ml-auto text-arena-900 dark:text-white">
              {a.direct_agents}<span className="text-arena-500 dark:text-neon-200/50 font-normal"> {t('agent.subAgentsList.row.directLabel')}</span> · {a.indirect_agents}<span className="text-arena-500 dark:text-neon-200/50 font-normal"> {t('agent.subAgentsList.row.indirectLabel')}</span>
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <Icon name="person" size={12} className="text-cyan-600 dark:text-cyan-400" />
            <span className="text-arena-600 dark:text-neon-200/70">{t('agent.subAgentsList.column.players')}</span>
            <span className="font-bold tabular-nums ml-auto text-arena-900 dark:text-white">
              {a.direct_players}<span className="text-arena-500 dark:text-neon-200/50 font-normal"> {t('agent.subAgentsList.row.directLabel')}</span> · {a.indirect_players}<span className="text-arena-500 dark:text-neon-200/50 font-normal"> {t('agent.subAgentsList.row.indirectLabel')}</span>
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <Icon name="account_balance_wallet" size={12} className="text-violet-600 dark:text-violet-400" />
            <span className="text-arena-600 dark:text-neon-200/70">{t('agent.subAgentsList.column.pool')}</span>
            <span className={`font-bold tabular-nums ml-auto ${a.wallet_balance >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-600 dark:text-red-400'}`}>
              {inr(a.wallet_balance)}
            </span>
          </div>
        </div>

        <Icon name="chevron_right" size={16} className="hidden md:block text-arena-500 dark:text-neon-200/50 shrink-0" />
      </div>
    </Link>
  );
}

function CreateSubAgentModal({ childLevelName, onClose, onCreated }: { childLevelName: string; onClose: () => void; onCreated: () => void }) {
  const t = useT();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  // 2026-05-18 — Identity hardening. Sub-agent creation now MANDATES
  // first_name + last_name + birthdate. Backend Zod schema rejects
  // any payload missing them, and the DB CHECK constraints back it.
  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [geo, setGeo] = useState<GeoBag>(EMPTY_GEO_BAG);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [rates, setRates] = useState<CommissionRates>({});
  // The MAX this agent may grant a sub-agent = its OWN rates (Golden Limit: child ≤
  // parent; win/loss is strict → own − 0.01). Shown as per-field ceilings + enforced.
  const [ceilings, setCeilings] = useState<Partial<Record<keyof CommissionRates, number>> | undefined>(undefined);
  useEffect(() => {
    adminApi.get<{ winLossPct: number; profitSharePct: number; perSport?: { cricket: number; football: number; tennis: number } }>('/admin/me-agent/my-commission-rates')
      .then((r) => setCeilings({
        winLossPct: Math.max(0, Math.round(r.winLossPct * 100 - 1) / 100),
        profitSharePct: r.profitSharePct,
        // C3 (2026-08-06): per-sport ceilings so the sub-agent create modal shows the
        // correct football/tennis cap (falls back to the shared cap if unset).
        cricketPct: r.perSport?.cricket,
        footballPct: r.perSport?.football,
        tennisPct: r.perSport?.tennis,
      }))
      .catch(() => {});
  }, []);

  const geoSet = isGeoBagComplete(geo);
  // 2026-05-25 — phone now mandatory + validated (unique on the server).
  const phoneValid = /^\+?\d{7,15}$/.test(phone.trim());
  const namesValid =
    firstName.trim().length >= 2 &&
    lastName.trim().length  >= 2 &&
    /^\d{4}-\d{2}-\d{2}$/.test(birthdate);

  const valid =
    username.trim().length >= 3 &&
    /^[A-Za-z0-9._-]+$/.test(username.trim()) &&
    /^\S+@\S+\.\S+$/.test(email.trim()) &&
    phoneValid &&
    password.length >= 8 &&
    namesValid;

  const submit = async () => {
    setErr(null); setBusy(true);
    try {
      const body: Record<string, unknown> = {
        username: username.trim(), email: email.trim(), password,
        phone: phone.trim(),
        // 2026-05-18 — Mandatory display identity. Trim is defensive;
        // the backend Zod also trims + enforces min(2) length.
        first_name: firstName.trim(),
        last_name:  lastName.trim(),
        birthdate,
      };
      if (geo.latitude    != null) body.latitude    = geo.latitude;
      if (geo.longitude   != null) body.longitude   = geo.longitude;
      if (geo.state_id    != null) body.state_id    = geo.state_id;
      if (geo.district_id != null) body.district_id = geo.district_id;
      if (geo.area_id     != null) body.area_id     = geo.area_id;
      if (geo.nation)              body.nation      = geo.nation;
      const rp = ratesPayload(rates);
      if (rp)                      body.rates       = rp;
      await adminApi.post('/admin/me-agent/sub-agents', body);
      onCreated();
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    // 2026-05-20 — Mobile scroll fix.
    // The old layout used the "outer overflow-y-auto + single body card"
    // pattern. On small phones with a tall form (firstName/lastName/
    // birthdate/username/email/password/location), the form extended
    // below the viewport and Safari's touch scroll on the outer fixed
    // wrapper was unreliable — users perceived the modal as un-scrollable.
    //
    // Switched to the two-tier scroll pattern that CreatePlayerModal
    // uses in shared-ui:
    //   • Outer fixed wrapper: keeps `overflow-y-auto` as a fallback
    //     for edge cases (e.g. extremely tall content beyond 100dvh).
    //   • Inner card: `flex flex-col` + `max-h-[calc(100dvh-1.5rem)]`
    //     so it stays within the visible viewport.
    //   • Body section between header and footer gets its own
    //     `overflow-y-auto` so the form fields scroll INSIDE the card
    //     while the header (title + close) and footer (action buttons)
    //     stay pinned. `overscroll-contain` prevents the body's scroll
    //     from chaining to the outer wrapper or the page underneath.
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-start sm:place-items-center p-3 sm:p-4 overflow-y-auto" role="dialog" aria-modal="true">
      <div className="bg-white dark:bg-arena-900 rounded-xl border border-arena-200/60 dark:border-arena-700/60 shadow-2xl
                      max-w-md w-full max-h-[calc(100dvh-1.5rem)] sm:max-h-[calc(100dvh-2rem)]
                      flex flex-col overscroll-contain my-4">
        {/* Header — pinned. */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 shrink-0 border-b border-arena-100 dark:border-arena-700/60">
          <div className="text-base font-bold text-arena-900 dark:text-white">New {childLevelName}</div>
          <button onClick={onClose} className="text-2xl leading-none text-arena-500 dark:text-neon-200/70 hover:text-arena-700 dark:hover:text-white">×</button>
        </div>

        {/* Body — scrolls when content overflows. */}
        <div className="overflow-y-auto px-5 py-4 space-y-4">
        <div className="text-[11px] text-arena-700 dark:text-neon-200/75 leading-relaxed">
          This account will be a <b className="text-arena-900 dark:text-white">{childLevelName}</b> under you. Set their partnership (P&amp;L) % below — capped to your own rate. You can adjust it later from their detail page.
        </div>

        {err && <div className="px-3 py-2 rounded bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-xs">{err}</div>}

        {/* 2026-05-18 — Mandatory identity fields (first/last/birthdate).
            Pre-named labels are intentionally English here; the i18n
            dict can add agent.subAgentsList.modal.field.* keys for
            firstName/lastName/birthdate in a later sweep. */}
        <div className="grid grid-cols-2 gap-2">
          <label className="block text-xs">
            <div className="text-arena-600 dark:text-neon-200/70 mb-1 font-bold uppercase tracking-wider text-[10px]">First name <span className="text-red-500">*</span></div>
            <input value={firstName} onChange={e => setFirstName(e.target.value)}
                   placeholder="Rajesh" maxLength={80}
                   className={INPUT_CLS} />
          </label>
          <label className="block text-xs">
            <div className="text-arena-600 dark:text-neon-200/70 mb-1 font-bold uppercase tracking-wider text-[10px]">Last name <span className="text-red-500">*</span></div>
            <input value={lastName} onChange={e => setLastName(e.target.value)}
                   placeholder="Sharma" maxLength={80}
                   className={INPUT_CLS} />
          </label>
        </div>
        <label className="block text-xs">
          <div className="text-arena-600 dark:text-neon-200/70 mb-1 font-bold uppercase tracking-wider text-[10px]">Date of birth <span className="text-red-500">*</span></div>
          <input type="date" value={birthdate} onChange={e => setBirthdate(e.target.value)}
                 className={INPUT_DATE_CLS} />
          <div className="text-[10px] text-arena-600 dark:text-neon-200/70 mt-1">Private — only seen by the sub-agent themselves.</div>
        </label>
        <label className="block text-xs">
          <div className="text-arena-600 dark:text-neon-200/70 mb-1 font-bold uppercase tracking-wider text-[10px]">{t('agent.subAgentsList.modal.field.username.label')} <span className="text-red-500">*</span></div>
          <input value={username} onChange={e => setUsername(e.target.value)}
                 placeholder={t('agent.subAgentsList.modal.field.username.placeholder')}
                 className={INPUT_CLS} />
          <div className="text-[10px] text-arena-600 dark:text-neon-200/70 mt-1">Login only — your sub-agent's downline / upline never sees this.</div>
        </label>
        <label className="block text-xs">
          <div className="text-arena-600 dark:text-neon-200/70 mb-1 font-bold uppercase tracking-wider text-[10px]">{t('agent.subAgentsList.modal.field.email.label')} <span className="text-red-500">*</span></div>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                 placeholder={t('agent.subAgentsList.modal.field.email.placeholder')}
                 className={INPUT_CLS} />
        </label>
        <label className="block text-xs">
          <div className="text-arena-600 dark:text-neon-200/70 mb-1 font-bold uppercase tracking-wider text-[10px]">Phone <span className="text-red-500">*</span></div>
          <input type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                 placeholder="9876543210" maxLength={16}
                 className={INPUT_CLS} />
          {phone.trim() && !phoneValid && (
            <div className="text-[10px] text-red-600 dark:text-red-300 mt-1">Enter a valid phone (7–15 digits, optional +).</div>
          )}
        </label>
        <label className="block text-xs">
          <div className="text-arena-600 dark:text-neon-200/70 mb-1 font-bold uppercase tracking-wider text-[10px]">{t('agent.subAgentsList.modal.field.password.label')} <span className="text-red-500">*</span></div>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                 placeholder={t('agent.subAgentsList.modal.field.password.placeholder')}
                 className={INPUT_CLS} />
          <div className="text-[10px] text-arena-600 dark:text-neon-200/70 mt-1">{t('agent.subAgentsList.modal.field.password.hint')}</div>
        </label>

        {/* R4 Step 2 — assign the new sub-agent's rough area.
            GPS HIDDEN: parent's coords would mis-attribute future
            cold signups in the sub-agent's territory. Sub-agent
            overlays precise lat/lng themselves via GeoForcedModal
            on first login. */}
        <div className="pt-1">
          <div className="text-arena-600 dark:text-neon-200/70 mb-1.5 font-bold uppercase tracking-wider text-[10px]">
            {t('agent.subAgentsList.modal.field.location.label')} <span className="text-arena-500 dark:text-neon-200/60 font-normal normal-case">{t('agent.subAgentsList.modal.field.location.optional')}</span>
          </div>
          <LocationCapture
            value={geo}
            onChange={setGeo}
            mode="child"                        // creator-mode: pincode only, no GPS
            showSavedSummary={false}            // new sub-agent has no value yet; go straight to editor
            disabled={busy}
            inputClassName={INPUT_CLS}
          />
          {!geoSet && (
            <p className="text-[10px] text-amber-700 dark:text-amber-300 mt-1.5 leading-relaxed">
              {t('agent.subAgentsList.modal.field.location.missing')}
            </p>
          )}
        </div>

        {/* Commission rates — set the new sub-agent's cut at creation. Each is
            automatically capped to your own rate (child ≤ parent). */}
        <CommissionRatesFields value={rates} onChange={(patch) => setRates((r) => ({ ...r, ...patch }))} ceilings={ceilings} disabled={busy} />

        </div>{/* /body */}

        {/* Footer — pinned. */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 shrink-0 border-t border-arena-100 dark:border-arena-700/60 bg-white/80 dark:bg-arena-900/80 backdrop-blur">
          <button onClick={onClose} disabled={busy} className="px-3 py-2 text-xs uppercase tracking-wider text-arena-700 dark:text-neon-200/80 hover:underline">{t('agent.subAgentsList.modal.cta.cancel')}</button>
          <button onClick={submit} disabled={!valid || busy}
                  className="px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50">
            {busy ? t('agent.subAgentsList.modal.cta.creating') : t('agent.subAgentsList.modal.cta.create')}
          </button>
        </div>
      </div>
    </div>
  );
}
